import {
    DEFAULT_SEED_EMOJI,
    DEFAULT_UPVALUE_EMOJI,
    isDefaultUpvalueEmoji,
} from 'components/votes/upvalueEmoji'
import type { ReactionOption } from 'components/votes/reactions'
import type { Answer } from 'types/answers'

export const ORDINAL_CHAT_REACTION_FALLBACK_EMOJIS = [
    '🔥',
    '💯',
    '✨',
    '😂',
    '🤔',
    '👀',
    '🙏',
    '🚀',
    '👍',
    '👎',
    '❤️',
    '😮',
    '😢',
    '🙌',
    '🎉',
    '💡',
    '💪',
    '😡',
    '😅',
    '🥲',
    '👏',
    '🤯',
    '🧠',
    '🫡',
    '😍',
    '🥹',
    '😎',
    '🤝',
    '💎',
    '🏆',
    '🎯',
    '⚡',
] as const

export const ORDINAL_CHAT_SATS_PRESETS = [
    { label: '10K', sats: 10_000 },
    { label: '100K', sats: 100_000 },
    { label: '1M', sats: 1_000_000 },
    { label: '10M', sats: 10_000_000 },
    { label: '100M', sats: 100_000_000 },
] as const

export const ORDINAL_CHAT_USD_PRESETS = [
    { label: '$0.001', usd: 0.001 },
    { label: '$0.01', usd: 0.01 },
    { label: '$0.10', usd: 0.1 },
    { label: '$1.00', usd: 1 },
    { label: '$10', usd: 10 },
] as const

export type OrdinalChatSignalSummary = {
    count: number
    emoji: string
    sats: number
    upvalueSats: number
    seedSats: number
    sentiments: Array<{ emoji: string; count: number; sats: number }>
    seeders: Array<{ userId: string; sats: number }>
}

export type OrdinalChatAmount = {
    sats: number
    usd: number | null
}

export function createOrdinalChatAmountSession() {
    const amounts = new Map<string, OrdinalChatAmount>()
    const listeners = new Set<() => void>()
    const keyFor = (useUsd: boolean) => (useUsd ? 'usd' : 'sats')
    const notify = () => listeners.forEach(listener => listener())

    return {
        resolve(useUsd: boolean, fallback: OrdinalChatAmount) {
            return amounts.get(keyFor(useUsd)) || fallback
        },
        snapshot(useUsd: boolean) {
            return amounts.get(keyFor(useUsd)) || null
        },
        remember(useUsd: boolean, amount: OrdinalChatAmount) {
            const key = keyFor(useUsd)
            const current = amounts.get(key)
            if (current?.sats === amount.sats && current.usd === amount.usd) return
            amounts.set(key, { ...amount })
            notify()
        },
        reset() {
            if (!amounts.size) return
            amounts.clear()
            notify()
        },
        subscribe(listener: () => void) {
            listeners.add(listener)
            return () => listeners.delete(listener)
        },
    }
}

export function summarizeOrdinalChatSignals(answer: Answer): OrdinalChatSignalSummary {
    const emojiTotals = new Map<string, { count: number; sats: number }>()
    const seederTotals = new Map<string, number>()
    let upvalueSats = 0
    let seedSats = 0

    for (const upvalue of answer.bsv_upvalues || []) {
        const amount = Number(upvalue.amount) || 0
        if (upvalue.is_seed) {
            seedSats += amount
            const userId = String(upvalue.from_user_id || '')
            seederTotals.set(userId, (seederTotals.get(userId) || 0) + amount)
            continue
        }

        const emoji = upvalue?.metadata?.sentiment_emoji || DEFAULT_UPVALUE_EMOJI
        const current = emojiTotals.get(emoji) || { count: 0, sats: 0 }
        current.count += 1
        current.sats += amount
        emojiTotals.set(emoji, current)
        upvalueSats += amount
    }

    for (const reaction of answer.reactions || []) {
        const emoji = reaction.emoji || DEFAULT_UPVALUE_EMOJI
        const current = emojiTotals.get(emoji) || { count: 0, sats: 0 }
        current.count += 1
        emojiTotals.set(emoji, current)
    }

    const sentiments = Array.from(emojiTotals.entries())
        .sort(([, left], [, right]) => right.sats - left.sats || right.count - left.count)
        .map(([emoji, value]) => ({ emoji, ...value }))
    const dominantEmoji = sentiments[0]?.emoji || DEFAULT_UPVALUE_EMOJI

    return {
        count: sentiments.reduce((total, value) => total + value.count, 0),
        emoji: dominantEmoji,
        sats: upvalueSats + seedSats,
        upvalueSats,
        seedSats,
        sentiments,
        seeders: Array.from(seederTotals.entries())
            .map(([userId, sats]) => ({ userId, sats }))
            .sort((left, right) => right.sats - left.sats),
    }
}

export function ordinalChatSeedGradient(seedSats: number, totalSats: number): string | undefined {
    if (seedSats <= 0 || totalSats <= 0) return undefined
    const share = Math.min(100, Math.max(0, (seedSats / totalSats) * 100))
    const feather = Math.min(14, Math.max(6, share * 0.18))
    const middle = Math.max(0, share - feather)
    const end = Math.min(100, share + feather)
    return `linear-gradient(90deg, var(--ordinal-seed-strong) 0%, var(--ordinal-seed-mid) ${middle}%, var(--ordinal-seed-soft) ${share}%, var(--color-surface-base) ${end}%)`
}

export function ordinalChatViewerHasSeed(
    summary: OrdinalChatSignalSummary,
    viewerId: string | number | null | undefined,
): boolean {
    if (viewerId == null) return false
    return summary.seeders.some(seeder => seeder.userId === String(viewerId) && seeder.sats > 0)
}

export function projectedOrdinalChatSeedShare({
    seedSats,
    viewerSeedSats,
    nextSeedSats,
}: {
    seedSats: number
    viewerSeedSats: number
    nextSeedSats: number
}): number {
    const nextTotal = seedSats + nextSeedSats
    if (nextTotal <= 0) return 0
    return ((viewerSeedSats + nextSeedSats) / nextTotal) * 100
}

export function buildOrdinalChatReactionOptions({
    recentEmojis,
    searchResults,
    searching,
    resolveOption,
    includeDefaultUpvalue = true,
    limit = 18,
}: {
    recentEmojis: string[]
    searchResults: ReactionOption[]
    searching: boolean
    resolveOption: (emoji: string) => ReactionOption
    includeDefaultUpvalue?: boolean
    limit?: number
}) {
    const emojiSource = searching
        ? searchResults
        : [
              ...recentEmojis.map(resolveOption),
              ...ORDINAL_CHAT_REACTION_FALLBACK_EMOJIS.map(resolveOption),
          ]
    const source = includeDefaultUpvalue
        ? [resolveOption(DEFAULT_UPVALUE_EMOJI), ...emojiSource]
        : emojiSource
    const seen = new Set<string>()

    return source
        .filter(option => {
            if (!option?.emoji) return false
            if (option.emoji === DEFAULT_SEED_EMOJI) return false
            if (!includeDefaultUpvalue && isDefaultUpvalueEmoji(option.emoji)) return false
            const optionKey = isDefaultUpvalueEmoji(option.emoji)
                ? DEFAULT_UPVALUE_EMOJI
                : option.emoji
            if (seen.has(optionKey)) return false
            seen.add(optionKey)
            return true
        })
        .slice(0, limit)
}
